<template>
  <b-container class="text-left">
    <h1 v-if="this.isImaginaryCompanyAlreadyExists" class="mt-2 mb-4">Tervezett vállalkozás szerkesztése</h1>
    <h1 v-else class="mt-2 mb-4">Új vállalkozás tervezése</h1>
    <p v-show="!this.isImaginaryCompanyAlreadyExists">
      Tervezze meg leendő vállalkozását, hogy megtudhassa, potenciálisan milyen forrásokat vehet igénybe.
    </p>

    <CompanyAttributeSection title="Vállalkozás neve és formája" description="">
      <b-row class="d-flex">
        <b-col xl="6" lg="6" md="6" sm="6" class="mb-4">
          <label class="label-p-m-reset" for="imaginaryCompanyName"
            >Tervezett cég neve<span class="required-star"></span
          ></label>
          <b-form-input
            id="imaginaryCompanyName"
            v-model="imaginaryCompanyName"
            placeholder="Cég neve"
            trim
            :state="companyNameState"
            :class="`${companyNameState !== null && !companyNameState ? 'create-company-error-border' : ''}`"
          ></b-form-input>
          <b-form-invalid-feedback class="create-company-error-message" id="input-live-feedback">
            Adja meg leendő cégének a nevét
          </b-form-invalid-feedback>
        </b-col>
        <b-col xl="6" lg="6" md="6" sm="6">
          <label class="label-p-m-reset" for="imaginaryCompanyForm"
            >Vállalkozás formája<span class="required-star"></span
          ></label>
          <b-form-group>
            <b-form-select
              class="dropdown-form"
              v-model="imaginaryCompanyForm"
              :options="companyFormOptions"
              id="imaginaryCompanyForm"
              :state="companyFormState"
              :class="`${companyFormState !== null && !companyFormState ? 'create-company-error-border' : ''}`"
            >
              <template #first>
                <b-form-select-option id="imaginaryCompanyForm" :value="null" disabled
                  >Vállalkozás formája</b-form-select-option
                >
              </template>
            </b-form-select>
            <b-form-invalid-feedback class="create-company-error-message" id="input-live-feedback">
              Válasszon vállalkozási formát
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>
    </CompanyAttributeSection>

    <CompanyAttributeSection title="Tevékenységi körök megadása" description="">
      <b-row>
        <b-col xl="6" lg="6" md="6" sm="6" class="mb-4">
          <label style="padding: 0">Válasszon fő tevékenységi kört <span class="required-star"></span></label>
          <TeaorSelector
            class="custom-selector teaor"
            @selected-teaor="selectTeaor($event)"
            listType="fourDigit"
            :alreadySelected="alreadySelectedTeaor"
            :showError="companyTeaorState !== null && !companyTeaorState"
          />
        </b-col>
        <b-col xl="6" lg="6" md="6" sm="6">
          <MultipleTeaorSelect
            listType="fourDigit"
            :alreadySelected="alreadySelectedTeaors"
            @selected-other-teaors="handleSelectedTeaors($event)"
            :maximumExtraFieldRule="5"
          />
        </b-col>
      </b-row>
    </CompanyAttributeSection>

    <CompanyAttributeSection title="Cég székhelyének és telephelyének megadása" description="">
      <b-row>
        <b-col xl="6" lg="6" md="6" sm="6" class="mb-4">
          <label style="padding: 0">Cég székhelyének irányítószáma<span class="required-star"></span></label>
          <ZipCodeSelector
            class="custom-selector"
            @selected-zipCode="selectZip($event)"
            :alreadySelected="alreadySelectedZip"
            :showError="companyAddressState !== null && !companyAddressState"
          />
        </b-col>
        <b-col xl="6" lg="6" md="6" sm="6">
          <MultipleZipSelect
            :alreadySelected="alreadySelectedZips"
            @selected-other-zips="handleSelectedZips($event)"
            :maximumExtraFieldRule="5"
          />
        </b-col>
      </b-row>
    </CompanyAttributeSection>

    <CompanyAttributeSection title="Cég gazdasági adatai" description="">
      <b-row>
        <b-col xl="6" lg="6" md="6" sm="6" class="mb-4">
          <label class="label-p-m-reset" for="imaginaryIncome"
            >Nettó árbevétel<span class="required-star"></span
          ></label>
          <div :class="`${touched ? '' : 'input-symbol-forint'}`">
            <b-form-input
              id="imaginaryIncome"
              v-model="imaginaryIncome"
              @keypress="isNumber($event, true)"
              placeholder="Tervezett nettó árbevétel"
              trim
              :state="companyIncomeState"
              :class="`${companyIncomeState !== null && !companyIncomeState ? 'create-company-error-border' : ''}`"
            ></b-form-input>
          </div>
          <b-form-invalid-feedback class="create-company-error-message" id="input-live-feedback">
            Adja meg a nettó árbevételt
          </b-form-invalid-feedback>
        </b-col>
        <b-col xl="6" lg="6" md="6" sm="6" class="mb-4">
          <label class="label-p-m-reset" for="imaginaryBalanceSheetTotal"
            >Mérlegfőösszeg<span class="required-star"></span
          ></label>
          <div :class="`${touched ? '' : 'input-symbol-forint'}`">
            <b-form-input
              id="imaginaryBalanceSheetTotal"
              v-model="imaginaryBalanceSheetTotal"
              @keypress="isNumber($event, true)"
              placeholder="Tervezett mérlegfőösszeg"
              trim
              :state="companyBalanceSheetTotalState"
              :class="
                `${
                  companyBalanceSheetTotalState !== null && !companyBalanceSheetTotalState
                    ? 'create-company-error-border'
                    : ''
                }`
              "
            ></b-form-input>
          </div>
          <b-form-invalid-feedback class="create-company-error-message" id="input-live-feedback">
            Adja meg a mérlegfőösszeget
          </b-form-invalid-feedback>
        </b-col>
      </b-row>
      <b-row>
        <b-col xl="6" lg="6" md="12" class="mb-4">
          <label class="label-p-m-reset" for="imaginaryHeadCount"
            >Átlagos statisztikai létszám<span class="required-star"></span
          ></label>
          <div :class="`${touched ? '' : 'input-symbol-person'}`">
            <b-form-input
              id="imaginaryHeadCount"
              v-model="imaginaryHeadCount"
              @keypress="isNumber($event, false)"
              placeholder="Tervezett létszám"
              trim
              :state="companyHeadCountState"
              :class="
                `${companyHeadCountState !== null && !companyHeadCountState ? 'create-company-error-border' : ''}`
              "
            ></b-form-input>
          </div>
          <b-form-invalid-feedback class="create-company-error-message" id="input-live-feedback">
            Adja meg a statisztikai létszámot
          </b-form-invalid-feedback>
        </b-col>

        <b-col xl="6" lg="6" md="12" class="mb-4">
          <label class="label-p-m-reset" for="imaginaryHeadCount">Export árbevétel</label>
          <div class="input-symbol-forint">
            <b-form-input
              id="imaginaryLatestExportRevenue"
              v-model="imaginaryLatestExportRevenue"
              @keypress="isNumber($event, true)"
              placeholder="Tervezett export árbevétel"
              trim
            ></b-form-input>
          </div>
        </b-col>
      </b-row>
    </CompanyAttributeSection>
    <b-row>
      <p class="m-3">A tervezett vállalkozást a Forrástérkép egy lezárt üzleti évvel és pozitív tőkével értékeli.</p>
      <br />
      <b-button class="btn ml-5 mt-5 link-button btn-primary" @click="prev"> Vissza </b-button>
      <b-button class="btn ml-3 mt-5 mb-5 link-button btn-primary" @click="next" variant="primary">
        {{ this.isImaginaryCompanyAlreadyExists ? 'Mentés' : 'Tovább' }}
      </b-button>
    </b-row>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex'
import TeaorSelector from '../../components/TeaorSelector/TeaorSelector.vue'
import ZipCodeSelector from '../../components/zipCodeSelector/zipCodeSelector.vue'
import CompanyAttributeSection from '../../components/CompanyAttributeSection.vue'
import MultipleZipSelect from './components/MultipleZipSelect.vue'
import MultipleTeaorSelect from './components/MultipleTeaorSelect.vue'
import * as ImaginaryCompanyApi from '../../api/imaginaryCompany'
import { gfoOptionsForFormSelect } from '../FundCreation/utils/gfoOptions'
import errorToast from '../../mixins/toastError'
import successToast from '../../mixins/toastSuccess'
import { regex } from '../../regex/regex'
export default {
  mixins: [successToast, errorToast],
  components: {
    CompanyAttributeSection,
    TeaorSelector,
    ZipCodeSelector,
    MultipleZipSelect,
    MultipleTeaorSelect
  },
  data() {
    return {
      companyFormOptions: gfoOptionsForFormSelect,
      imaginaryCompanyName: '',
      imaginaryCompanyForm: null,
      imaginaryTeaor: '',
      imaginaryAnotherTeaor: [],
      imaginaryAddress: '',
      imaginaryAnotherZip: [],
      imaginaryIncome: '',
      imaginaryBalanceSheetTotal: '',
      imaginaryHeadCount: '',
      imaginaryLatestExportRevenue: '',
      touched: false,
      isCreateCompanyFormFilled: false,
      isImaginaryCompanyAlreadyExists: false,
      idForEditImaginaryCompany: null,
      alreadySelectedTeaor: '',
      alreadySelectedZip: '',
      alreadySelectedTeaors: [],
      alreadySelectedZips: [],
      customMessage: 'Custom message 3'
    }
  },
  computed: {
    ...mapGetters(['isAuthenticated', 'fundMapCompany', 'getUser']),
    companyNameState() {
      if (this.touched) {
        return this.imaginaryCompanyName.length > 0
      } else {
        return null
      }
    },
    companyFormState() {
      if (this.touched) {
        return this.imaginaryCompanyForm != null
      } else {
        return null
      }
    },
    companyTeaorState() {
      if (this.touched) {
        return this.imaginaryTeaor.length > 0
      } else {
        return null
      }
    },
    companyAddressState() {
      if (this.touched) {
        return this.imaginaryAddress.length > 0
      } else {
        return null
      }
    },
    companyIncomeState() {
      if (this.touched) {
        return this.imaginaryIncome.length > 0
      } else {
        return null
      }
    },
    companyBalanceSheetTotalState() {
      if (this.touched) {
        return this.imaginaryBalanceSheetTotal.length > 0
      } else {
        return null
      }
    },
    companyHeadCountState() {
      if (this.touched) {
        return this.imaginaryHeadCount.length > 0 && Number(this.imaginaryHeadCount) >= 0
      } else {
        return null
      }
    }
  },
  methods: {
    isNumber(evt, isMinusAllowed) {
      const isStarting0Allowed = !this.$route.path.includes('/create-fund')
      const input = evt.target.value
      const keysAllowed = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']
      const keyPressed = evt.key

      if (keyPressed === '-' && input.length === 0 && isMinusAllowed) {
        return
      }

      if (input.length === 0 && keyPressed === '0' && isStarting0Allowed) {
        evt.preventDefault()
        return
      }

      if (!keysAllowed.includes(keyPressed)) {
        evt.preventDefault()
      }
    },

    validateField(value) {
      return regex.test(value)
    },
    addCompany(key, value) {
      this.$store.dispatch('setFundMapCompanyAction', { [key]: value })
    },
    selectTeaor(teaor) {
      this.imaginaryTeaor = teaor
    },
    selectAnotherTeaor(teaor) {
      this.imaginaryAnotherTeaor.push(teaor)
    },
    selectZip(zip) {
      this.imaginaryAddress = zip
    },
    selectAnotherZip(zip) {
      this.imaginaryAnotherZip.push(zip)
    },
    prev() {
      if (this.isImaginaryCompanyAlreadyExists) {
        this.$router.push('/profile')
      } else {
        this.$router.push('/fund-map')
      }
    },
    handleSelectedZips(value) {
      this.imaginaryAnotherZip = value
    },
    handleSelectedTeaors(value) {
      this.imaginaryAnotherTeaor = value
    },
    openLoginModal() {
      this.$root.$emit('bv::show::modal', 'login-modal')
    },
    async next() {
      const isValid =
        this.validateField(this.imaginaryCompanyName) &&
        this.validateField(this.imaginaryIncome) &&
        this.validateField(this.imaginaryBalanceSheetTotal) &&
        this.validateField(this.imaginaryHeadCount) &&
        this.validateField(this.imaginaryLatestExportRevenue)

      if (!isValid) {
        return this.errorToast(`A következő karakterek használata nem engedélyezett. (< > ; ' ")`)
      }
      if (
        this.imaginaryCompanyName.length > 0 &&
        this.imaginaryCompanyForm != null &&
        this.imaginaryTeaor.length > 0 &&
        this.imaginaryAddress.length > 0 &&
        this.imaginaryIncome.length > 0 &&
        this.imaginaryBalanceSheetTotal.length > 0 &&
        this.imaginaryHeadCount.length > 0
      ) {
        this.imaginaryCompany = {
          form: this.imaginaryCompanyForm,
          name: this.imaginaryCompanyName,
          mainTeaor: this.imaginaryTeaor,
          secondaryTeaors: this.imaginaryAnotherTeaor,
          address: this.imaginaryAddress,
          sites: this.imaginaryAnotherZip,
          income: this.imaginaryIncome,
          balanceSheetTotal: this.imaginaryBalanceSheetTotal,
          headCount: this.imaginaryHeadCount,
          latestExportRevenue: this.imaginaryLatestExportRevenue,
          isImaginaryCompany: true
        }
        this.addCompany('imaginaryCompany', this.imaginaryCompany)
        if (this.isAuthenticated) {
          try {
            if (this.isImaginaryCompanyAlreadyExists) {
              try {
                const result = await ImaginaryCompanyApi.patchImaginaryCompanyById(
                  this.idForEditImaginaryCompany,
                  this.imaginaryCompany
                )
                const imaginaryCompanyWithId = { companyId: result.companyId, ...this.imaginaryCompany }
                this.addCompany('imaginaryCompany', imaginaryCompanyWithId)
                this.successToast('A mentés sikeres volt!')
                setTimeout(() => {
                  this.$router.push('/fund-map/imaginary-company-details')
                }, 500)
              } catch (e) {
                console.log(e)
              }
            } else {
              const resp = await ImaginaryCompanyApi.saveImaginaryCompany(this.imaginaryCompany, this.getUser._id)
              const imaginaryCompanyWithId = { companyId: resp.companyId, ...this.imaginaryCompany }
              this.addCompany('imaginaryCompany', imaginaryCompanyWithId)
              this.$router.push('/fund-map/imaginary-company-details')
            }
          } catch (e) {
            console.log(e)
          }
        } else {
          this.openLoginModal()
        }
      } else {
        this.touched = true
        return this.errorToast('A továbblépéshez töltse ki a megjelölt mezőket!')
      }
      return
    },
    async getImaginaryCompanyByCompanyId(companyId) {
      try {
        await ImaginaryCompanyApi.getImaginaryCompanyByCompanyId(companyId)
        this.isImaginaryCompanyAlreadyExists = true
        this.idForEditImaginaryCompany = this.fundMapCompany.imaginaryCompany.companyId
      } catch (e) {
        console.log(e)
      }
    }
  },
  created() {
    this.getImaginaryCompanyByCompanyId(this.fundMapCompany.imaginaryCompany.companyId)

    if (this.fundMapCompany.imaginaryCompany.form) {
      this.imaginaryCompanyForm = this.fundMapCompany.imaginaryCompany.form
    }

    if (this.fundMapCompany.imaginaryCompany.name) {
      this.imaginaryCompanyName = this.fundMapCompany.imaginaryCompany.name
    }

    if (this.fundMapCompany.imaginaryCompany.mainTeaor) {
      this.imaginaryTeaor = this.fundMapCompany.imaginaryCompany.mainTeaor
      this.alreadySelectedTeaor = this.fundMapCompany.imaginaryCompany.mainTeaor
    }

    if (this.fundMapCompany.imaginaryCompany.secondaryTeaors) {
      this.imaginaryAnotherTeaor = this.fundMapCompany.imaginaryCompany.secondaryTeaors
      this.alreadySelectedTeaors = this.fundMapCompany.imaginaryCompany.secondaryTeaors
    }

    if (this.fundMapCompany.imaginaryCompany.address) {
      this.imaginaryAddress = this.fundMapCompany.imaginaryCompany.address
      this.alreadySelectedZip = this.fundMapCompany.imaginaryCompany.address
    }

    if (this.fundMapCompany.imaginaryCompany.sites) {
      this.imaginaryAnotherZip = this.fundMapCompany.imaginaryCompany.sites
      this.alreadySelectedZips = this.fundMapCompany.imaginaryCompany.sites
    }

    if (this.fundMapCompany.imaginaryCompany.income) {
      this.imaginaryIncome = this.fundMapCompany.imaginaryCompany.income
    }

    if (this.fundMapCompany.imaginaryCompany.balanceSheetTotal) {
      this.imaginaryBalanceSheetTotal = this.fundMapCompany.imaginaryCompany.balanceSheetTotal
    }

    if (this.fundMapCompany.imaginaryCompany.headCount) {
      this.imaginaryHeadCount = this.fundMapCompany.imaginaryCompany.headCount
    }

    if (this.fundMapCompany.imaginaryCompany.latestExportRevenue) {
      this.imaginaryLatestExportRevenue = this.fundMapCompany.imaginaryCompany.latestExportRevenue
    }

    if (this.fundMapCompany.imaginaryCompany.companyData.form) {
      this.imaginaryCompanyForm = this.fundMapCompany.imaginaryCompany.companyData.form
    }

    if (this.fundMapCompany.imaginaryCompany.companyData.name) {
      this.imaginaryCompanyName = this.fundMapCompany.imaginaryCompany.companyData.name
    }

    if (this.fundMapCompany.imaginaryCompany.companyData.mainTeaor) {
      this.imaginaryTeaor = this.fundMapCompany.imaginaryCompany.companyData.mainTeaor
      this.alreadySelectedTeaor = this.fundMapCompany.imaginaryCompany.companyData.mainTeaor
    }

    if (this.fundMapCompany.imaginaryCompany.companyData.secondaryTeaors) {
      this.imaginaryAnotherTeaor = this.fundMapCompany.imaginaryCompany.companyData.secondaryTeaors
      this.alreadySelectedTeaors = this.fundMapCompany.imaginaryCompany.companyData.secondaryTeaors
    }

    if (this.fundMapCompany.imaginaryCompany.companyData.address) {
      this.imaginaryAddress = this.fundMapCompany.imaginaryCompany.companyData.address
      this.alreadySelectedZip = this.fundMapCompany.imaginaryCompany.companyData.address
    }

    if (this.fundMapCompany.imaginaryCompany.companyData.sites) {
      this.imaginaryAnotherZip = this.fundMapCompany.imaginaryCompany.companyData.sites
      this.alreadySelectedZips = this.fundMapCompany.imaginaryCompany.companyData.sites
    }

    if (this.fundMapCompany.imaginaryCompany.companyData.income) {
      this.imaginaryIncome = this.fundMapCompany.imaginaryCompany.companyData.income
    }

    if (this.fundMapCompany.imaginaryCompany.companyData.balanceSheetTotal) {
      this.imaginaryBalanceSheetTotal = this.fundMapCompany.imaginaryCompany.companyData.balanceSheetTotal
    }

    if (this.fundMapCompany.imaginaryCompany.companyData.headCount) {
      this.imaginaryHeadCount = this.fundMapCompany.imaginaryCompany.companyData.headCount
    }

    if (this.fundMapCompany.imaginaryCompany.companyData.latestExportRevenue) {
      this.imaginaryLatestExportRevenue = this.fundMapCompany.imaginaryCompany.companyData.latestExportRevenue
    }
  },
  mounted() {
    window.onbeforeunload = function(e) {
      e.returnValue = ''
      return ''
    }
  },
  beforeDestroy() {
    window.onbeforeunload = null
  }
}
</script>

<style lang="scss" scoped>
.dropdown-form {
  border-width: 2px !important;
}

#imaginaryIncome::placeholder {
  color: #636e72 !important;
}

.custom-selector {
  margin-top: -3px;
}
@media (max-width: 767px) {
  label {
    height: 30px;
  }
}

.btn-primary {
  min-width: 150px;
  float: left;
}

.create-company-error-border {
  border: 2px solid #ba3b46 !important;
}

.create-company-error-message {
  color: #ba3b46;
  text-align: right;
  font-size: 12px;
}

.form-control.is-invalid {
  background-image: unset !important;
}

.label-p-m-reset {
  padding: 0;
  margin: 0;
}
</style>
